import React, { useEffect, useReducer } from 'react'
import { useTranslation } from 'i18n'

import { getGalleryItems, vote } from 'services/gallery'
import GalleryModal from './components/GalleryModal'
import GalleryScreen from './components/Gallery'
import analytics from 'global/AnalyticsData'
import queryString from 'query-string'
import ShareIcons from 'components/ShareIcons'
import './gallery-step.scss'

const galleryReducer = (state, action) => {
  switch (action.type) {
    case 'LOADED_PAGE':
      return {
        ...state,
        galleryItems: state.galleryItems.concat(action.content),
        currentPage: action.pageNumber,
        hasMoreToLoad: action.hasMoreToLoad,
      }
    case 'SHOW_ITEM':
      return {
        ...state,
        modelItem: action.modalItem,
        isModalOpen: true,
      }
    case 'CLOSE_MODAL':
      return {
        ...state,
        modelItem: null,
        isModalOpen: false,
      }
    case 'VOTE':
      return {
        ...state,
        galleryItems: action.galleryItems,
      }
    default:
      console.warn(`unhandled action.type: ${action.type}`)
      return state
  }
}

const GalleryStep = ({
  breakId,
  brk,
  step,
  onEngage = function () {},
  onAwardPoints = function () {},
  userType,
  service,
  nextStepButton,
  themeColors,
  skipButton,
}) => {
  const { t } = useTranslation('gallery')
  let initialState = {
    isLoading: true,
    galleryItems: [],
    currentPage: -1,
    hasMoreToLoad: true,
    isModalOpen: false,
    columns: step.gallery.columns || 4,
    size: step.gallery.perPage || 12,
  }

  const [state, dispatch] = useReducer(galleryReducer, initialState)

  const addPage = async pageNumber => {
    try {
      const result = await getGalleryItems({
        page: pageNumber,
        size: state.size,
        breakId: breakId,
        stepId: step.id,
        groupType: step.gallery.groupType,
        sourceCategory: step.gallery.sourceCategory,
      })

      // define shareUrls for each item so they know how to share themselves
      const galleryItems = result.content.map(item => {
        const shareUrl = `${process.env.DAILYBREAK_API_URL}/gallery/${item.id}/share?itemId=${item.id}&permalink=${brk.permalink}&stepId=${step.id}`

        return {
          ...item,
          shareUrl: shareUrl,
        }
      })

      dispatch({
        type: 'LOADED_PAGE',
        content: galleryItems,
        pageNumber: pageNumber,
        hasMoreToLoad: !result.last,
      })
    } catch (e) {
      console.log(e)
    }
  }

  const checkQuery = () => {
    try {
      if (window !== undefined) {
        console.log(`checking query`)

        let query = queryString.parse(window.location.search)

        if (query && query.galleryitem) {
          let item = state.galleryItems.find(
            item => item.id.toString() === query.galleryitem,
          )
          console.log(`found item in querysting`, item, query.galleryitem)

          if (item) {
            dispatch({
              type: 'SHOW_ITEM',
              modalItem: item,
            })

            analytics.stepEvent('step_gallery_inbound', step, {
              result_id: item.id,
              result: item.title,
            })
          }
        }
      }
    } catch (error) {
      console.log(`error`, error)
    }
  }

  useEffect(() => {
    addPage(0)
  }, [])

  useEffect(() => {
    checkQuery()
  }, [state.galleryItems])

  const handleLoadMoreClicked = () => {
    analytics.stepEvent('step_interaction_load_more_clicked', step, {
      context_id: state.currentPage,
      context: state.currentPage,
      result_id: state.currentPage + 1,
      result: state.currentPage + 1,
    })
    addPage(state.currentPage + 1)
  }

  const handleVoteClick = itemId => {
    vote(breakId, step.id, itemId, step.gallery.sourceCategory)

    const itemVoteFor = state.galleryItems.find(item => {
      return item.id === itemId
    })

    analytics.stepEvent('step_interaction_gallery_vote', step, {
      result_id: itemId,
      result: itemVoteFor.title,
    })

    const newGalleryItems = state.galleryItems.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          userVotedToday: true,
          voteCount: item.voteCount + 1,
        }
      }
      return item
    })
    dispatch({
      type: 'VOTE',
      galleryItems: newGalleryItems,
    })

    if (state.isModalOpen) {
      const modalItem = state.galleryItems.find(item => {
        return item.id === itemId
      })
      dispatch({
        type: 'SHOW_ITEM',
        modalItem: {
          ...modalItem,
          userVotedToday: true,
          voteCount: modalItem.voteCount + 1,
        },
      })
    }
    onEngage()
    onAwardPoints()
  }

  const handleItemClick = id => {
    console.log(`handleItemClick`, id)

    const itemClicked = state.galleryItems.find(item => {
      return item.id === id
    })

    analytics.stepEvent('step_interaction_gallery_modal_open', step, {
      result_id: id,
      result: itemClicked.title,
    })

    const modalItem = state.galleryItems.find(item => {
      return item.id === id
    })

    dispatch({
      type: 'SHOW_ITEM',
      modalItem: modalItem,
    })
  }

  const handleCloseModalClick = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    })
  }

  const handleShareClick = args => {
    analytics.stepEvent('step_share_click', step, {
      context_id: args.platform,
      context: args.platform,
    })
  }

  let shareUrl = ''
  if (typeof window !== 'undefined') {
    shareUrl = window.location.href
    shareUrl = shareUrl.includes('?')
      ? shareUrl + `&stepId=${step.id}`
      : shareUrl + `?stepId=${step.id}`
  }
  const socialShareComponent = step.socialShareOn && (
    <ShareIcons
      facebookShare={step.facebookShare}
      twitterShare={step.twitterShare}
      pinterestShare={step.pinterestShare}
      emailShare={step.emailShare}
      emailConfig={{
        subject: step.shareEmailSubject,
        body: step.shareEmailBody,
      }}
      copyShare={step.copyShare}
      whatsappShare={step.whatsappShare}
      shareUrl={shareUrl}
      onShareClick={handleShareClick}
    />
  )

  return (
    <>
      <section className={`gallery-step new-gallery-step`}>
        <div
          className={`step-content with-shadow ${
            brk?.navigationType === 'TOP' ? 'top-navigation' : ''
          }`}
        >
          <div className="gallery-step__content">
            <div className="step-intro step-subcopy">
              {step.gallery.title && (
                <h2
                  className="step-content__title"
                  dangerouslySetInnerHTML={{ __html: step.gallery.title }}
                />
              )}
              <div
                className="step-content__intro"
                dangerouslySetInnerHTML={{ __html: step.gallery.copy }}
              />
            </div>

            <GalleryScreen
              hasVoting={step.gallery.hasVoting}
              items={state.galleryItems}
              onItemClick={handleItemClick}
              onVoteClick={handleVoteClick}
              zeroVotesCopy={step.gallery.zeroVotesCopy}
              galleryWidth={state.columns}
            />
            {state.modelItem && (
              <GalleryModal
                isModalOpen={state.isModalOpen}
                item={state.modelItem}
                hasVoting={step.gallery.hasVoting}
                userCanVote={!state.modelItem.userVotedToday}
                onVoteClick={handleVoteClick}
                onCloseModalClick={handleCloseModalClick}
                zeroVotesCopy={step.gallery.zeroVotesCopy}
              />
            )}

            {state.hasMoreToLoad && (
              <div style={{ marginBottom: '48px', textAlign: 'center' }}>
                <button
                  className={'btn themed-button'}
                  onClick={handleLoadMoreClicked}
                >
                  {t('loadMore')}
                </button>
              </div>
            )}
            {socialShareComponent}
          </div>
          {step.gallery.footerCopy && (
            <div
              className="step-content__footer"
              dangerouslySetInnerHTML={{ __html: step.gallery.footerCopy }}
            />
          )}
          {skipButton}
          {nextStepButton}
        </div>
      </section>
    </>
  )
}

export default GalleryStep
